import React, { useState } from "react";
import { Button, Drawer, Flex, Form, type FormInstance, Row } from "antd";
import { CreateAndExport, type CreateExportConfig } from "./CreateAndExport.tsx";
import { SearchBar } from "./SearchBar.tsx";
import type { IntlShape } from "@formatjs/intl";
import { FilterOutlined } from "@ant-design/icons";

type FilterRecord = {
  [key: string]: string;
};
type EmptyObject = Record<never, never>;
type FilterType = FilterRecord | EmptyObject;

type ListActionBarProps = {
  intl: IntlShape;
  form: FormInstance;
  children?: React.ReactNode;
  onFilter: () => void;
  onCancel: () => void;
  filterTemplate: FilterType;
  currentFilter: FilterType;
  searchBarPlaceholderKey: string;
  initialOpen?: boolean;
  createExportConfig?: CreateExportConfig;
  searchName?: string;
  filterDisabled?: boolean;
};

export function ListActionBar(props: ListActionBarProps) {
  const {
    intl,
    form,
    children,
    onFilter,
    onCancel,
    filterTemplate,
    currentFilter,
    searchBarPlaceholderKey,
    initialOpen = false,
    createExportConfig = {},
    filterDisabled = false,
    searchName,
  } = props;
  const [isOpen, setIsOpen] = useState(initialOpen);

  const isFiltered = Object.keys(filterTemplate).some((key) => key in currentFilter);

  return (
    <>
      <Flex style={{ width: "100%" }} align="center" gap={8} wrap>
        <Flex flex={1} align="center" gap={8} style={{ minWidth: 0 }}>
          <Flex flex={1} style={{ minWidth: 0 }}>
            <SearchBar
              placeholder={searchBarPlaceholderKey}
              onFilter={onFilter}
              searchName={searchName}
              form={form}
            />
          </Flex>
          <Flex gap={4} flex="none">
            {!filterDisabled ? (
              <Button
                color="default"
                variant="filled"
                icon={<FilterOutlined />}
                onClick={() => setIsOpen((state) => !state)}
              >
                {intl.formatMessage({ id: "filter_header_name" })}
              </Button>
            ) : null}

            {isFiltered && (
              <Button color="danger" variant="filled" onClick={onCancel}>
                {intl.formatMessage({ id: "filter_header_clear_button" })}
              </Button>
            )}
          </Flex>
        </Flex>

        <CreateAndExport createExportConfig={createExportConfig} />
      </Flex>
      <Drawer
        title={intl.formatMessage({ id: "filter_header_name" })}
        onClose={() => {
          setIsOpen(false);
        }}
        open={isOpen}
        styles={{
          wrapper: { width: "min(750px, 95vw)" },
          body: {
            paddingBottom: "var(--ant-padding-xl)",
            width: "100%",
          },
        }}
        extra={
          <Flex justify="space-between" align="center" gap={8}>
            <Button
              type="default"
              onClick={() => {
                setIsOpen(false);
                onCancel();
              }}
            >
              {intl.formatMessage({ id: "filter_header_clear_button" })}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setIsOpen(false);
                onFilter();
              }}
            >
              {intl.formatMessage({ id: "filter_header_filter_button" })}
            </Button>
          </Flex>
        }
      >
        <Form
          layout="vertical"
          name="FilterDrawerForm"
          form={form}
          initialValues={currentFilter}
        >
          <Row gutter={[16, 0]}>{children}</Row>
        </Form>
      </Drawer>
    </>
  );
}
